import { useRef, useState } from 'react';
import type { SimpleCostSegment } from '../../types';
import { fromMinor, money, toMinor } from '../../core';
import { newId, useActions } from '../../store';
import { TextField, MoneyField, Stepper, NoteField } from './fields';
import formShell from './formShell.module.css';
import { useT } from '../../i18n';

export interface SimpleCostFormProps {
  mode: 'create' | 'edit';
  kind: 'taxi' | 'food' | 'misc';
  segment?: SimpleCostSegment;
  onClose(): void;
}

export function SimpleCostForm({ mode, kind, segment, onClose }: SimpleCostFormProps) {
  const t = useT();
  const actions = useActions();

  const [label, setLabel] = useState(segment?.label ?? '');
  const [city, setCity] = useState(segment?.city ?? '');
  const [amount, setAmount] = useState(segment ? String(fromMinor(segment.price.amount)) : '');
  const [currency, setCurrency] = useState(segment?.price.currency ?? 'USD');
  const [quantity, setQuantity] = useState(segment?.quantity ?? 1);
  const [note, setNote] = useState(segment?.note ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const refs = useRef<Record<string, HTMLElement | null>>({});
  const quantityLabel = t(kind === 'food' ? 'field.days' : 'field.quantity');

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!label.trim()) errs.label = t('error.labelRequired');
    if (quantity < 1) errs.quantity = t('error.quantityMin');
    const amountNum = parseFloat(amount.replace(',', '.'));
    if (amount.trim() === '' || Number.isNaN(amountNum) || amountNum < 0) {
      errs.amount = t('error.priceNegative');
    }
    return errs;
  }

  function focusFirstError(errs: Record<string, string>) {
    for (const key of ['label', 'amount']) {
      if (errs[key]) {
        refs.current[key]?.focus();
        return;
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    const built: SimpleCostSegment = {
      kind,
      id: segment?.id ?? newId(kind[0]),
      label: label.trim(),
      city: city.trim() || undefined,
      price: money(toMinor(amount), currency),
      quantity,
      note: note.trim() || undefined,
    };
    if (mode === 'edit' && segment) {
      actions.updateSegment(built.id, built);
    } else {
      actions.addSegment(built);
    }
    onClose();
  }

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        id="cost-label"
        label={t('field.label')}
        value={label}
        onChange={setLabel}
        required
        autoFocus
        error={errors.label}
        inputRef={(el) => {
          refs.current.label = el;
        }}
      />
      <TextField id="cost-city" label={t('field.city')} value={city} onChange={setCity} placeholder={t('form.optional')} />
      <MoneyField
        idPrefix="cost-price"
        label={t('field.price')}
        amount={amount}
        onAmountChange={setAmount}
        currency={currency}
        onCurrencyChange={setCurrency}
        required
        error={errors.amount}
        inputRef={(el) => {
          refs.current.amount = el;
        }}
      />
      <Stepper id="cost-quantity" label={quantityLabel} value={quantity} onChange={setQuantity} min={1} />
      <NoteField id="cost-note" value={note} onChange={setNote} />
      <div className={formShell.footer}>
        <button type="button" className="btn" onClick={onClose}>
          {t('form.cancel')}
        </button>
        <button type="submit" className="btn btn-primary">
          {t('form.save')}
        </button>
      </div>
    </form>
  );
}
